import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useColorMode } from '@chakra-ui/react';
import { t } from 'i18next';

import { useQueryParams } from '@pocapan/shared/utils/useQueryParams';
import { getEventCompleteImage } from '@pocapan/shared/utils/EventUtils';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';

import StickyHeader from '../../../components/StickyHeader';

import { Button } from '@peacecover/component';
import { Xmark } from '@peacecover/icon';

export default function ReportEventCompletePage() {
  const { queryParams } = useQueryParams();
  const navigate = useNavigate();
  const spotsCategory = queryParams.get('spotsCategory');
  const locationId = queryParams.get('locationId');
  const eventId = queryParams.get('eventId');

  const { colorMode } = useColorMode();
  const handleHistoryBack = useHandleHistoryBack();

  const navigateToGoods = () => {
    navigate(`/report-location/events/goods?${queryParams}`, {
      replace: true
    });
  };

  const navigateToHome = async () => {
    navigate('/collections?boxTabIndex=1', {
      replace: true
    });
  };

  const navigateToEventDetail = () => {
    if (spotsCategory === 'landmark' || spotsCategory === 'foodie_spot') {
      navigate(`/locations/${locationId}`);
      return;
    }
    navigate(`/locations/${locationId}/events/${eventId}`);
  };

  return (
    <>
      <Helmet>
        <title>{t('ReportEventCompletePage.pageName', { defaultValue: '제보하기' })}</title>
      </Helmet>
      <StickyHeader
        title={t('ReportEventCompletePage.pageName', { defaultValue: '제보하기' }) as string}
        alwaysShowTitle={true}
        showBackButton={false}
        showTitle={true}
        leftContent={<Xmark size={24} className="text-text-main-lv1" onClick={handleHistoryBack} />}
      />
      {/* 콘텐츠 */}
      <div
        className="flex flex-col min-h-screen pt-[calc(54px+env(safe-area-inset-top))] pb-[calc(128px+env(safe-area-inset-bottom))] bg-repeat"
        style={{
          backgroundImage: `url('/assets/backgrounds/report/report_complete_${colorMode === 'dark' ? 'dark' : 'light'}.png')`,
          backgroundRepeat: 'round'
        }}
      >
        <div className="flex flex-col items-center text-center mt-[31px] gap-[31px] z-[50]">
          <img
            src={getEventCompleteImage(spotsCategory || '')}
            alt={'Good.Card_wallet'}
            className="w-[100px] h-[100px]"
          />
          <div className="flex flex-col gap-[16px]">
            <div className="text-[26px] font-bold text-text-main-lv1 whitespace-pre-line leading-tight">
              {t(`ReportEventCompletePage.title.${spotsCategory}`, {
                defaultValue: '장소가\n성공적으로 등록되었어요!'
              })}
            </div>
            {spotsCategory === 'birthday_cafe' && (
              <div className="text-[15px] text-text-main-lv3">
                {t('ReportEventCompletePage.description', {
                  defaultValue: '특전으로 제공된 굿즈가 있다면, 이어서 등록해보세요'
                })}
              </div>
            )}
          </div>
        </div>
        <div className="absolute top-[390px] -left-[40px] animate-float">
          <img
            src={'/assets/icons/graphic-3d/3D.Sparkling heart.png'}
            alt={'Good.Card_wallet'}
            className="w-[213px] h-auto rotate-[12deg] transform"
          />
        </div>
        <div className="absolute top-[370px] right-0 animate-float ">
          <img
            src={'/assets/icons/graphic-3d/3D.Four leaf clover.png'}
            alt={'Good.Card_wallet'}
            className="w-[120px] h-[120px] rotate-[-14deg] transform"
          />
        </div>
        <div className="absolute bottom-[100px] right-[33px] animate-float ">
          <img
            src={'/assets/icons/graphic-3d/3D.Index pointing up.png'}
            alt={'Good.Card_wallet'}
            className="w-[150px] h-[150px] rotate-[-19deg] transform"
          />
        </div>

        <div className="fixed bottom-0 overflow-hidden w-full max-w-[var(--chakra-sizes-xl)] bg-transparent text-center z-[9999] px-[20px] pb-[calc(10px+env(safe-area-inset-bottom))]">
          {spotsCategory === 'birthday_cafe' ? (
            <div className="flex flex-col gap-2">
              <Button
                size="large"
                btnColor="primary"
                className="font-semibold text-[17px]"
                onClick={navigateToGoods}
              >
                {t('ReportEventCompletePage.addGoods', {
                  defaultValue: '굿즈 정보 추가하기'
                })}
              </Button>
              <Button
                size="large"
                btnColor="secondary"
                className="font-semibold text-[17px]"
                onClick={navigateToHome}
              >
                {t('ReportEventCompletePage.goHome', {
                  defaultValue: '홈으로 돌아가기'
                })}
              </Button>
            </div>
          ) : (
            <div className="flex flex-row gap-[7px]">
              <Button
                size="large"
                btnColor="secondary"
                className="font-semibold text-[17px] w-full px-[20px]"
                onClick={navigateToHome}
              >
                {t('ReportEventCompletePage.goFirst', {
                  defaultValue: '처음으로 돌아가기'
                })}
              </Button>
              <Button
                size="large"
                btnColor="primary"
                className="font-semibold text-[17px] w-full"
                onClick={navigateToEventDetail}
              >
                {t('ReportEventCompletePage.goEventDetail', {
                  defaultValue: '자세히 보기'
                })}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
